import React from 'react';

import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import { Box, Stack, Typography } from '@mui/material';

import { uniqueBy } from '~/utils/arrayUtils';

import { formatContextDate } from '../../shared/format';

import type { BlackoutDateRange, SurchargePeriodRowView } from './types';

interface Props {
  rows: Array<SurchargePeriodRowView>;
}

export default function SurchargeBlackoutOverlapBlock({ rows }: Props) {
  const rowsWithOverlaps = rows.filter((row) => row.blackoutOverlaps.length > 0);

  if (rowsWithOverlaps.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        bgcolor: 'background.default',
        border: 1,
        borderColor: 'warning.main',
        borderRadius: 1,
        mt: 2,
        p: 1.5,
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <FlagOutlinedIcon fontSize="small" sx={{ color: 'warning.main' }} />
        <Typography variant="body2" fontWeight={700}>
          Blackout / surcharge overlaps
        </Typography>
      </Stack>
      <Box component="ul" sx={{ m: 0, pl: 2.25 }}>
        {rowsWithOverlaps.map((row) => {
          const uniqueBlackoutOverlaps = dedupeBlackoutOverlaps(row.blackoutOverlaps);

          return (
            <Box key={row.id} component="li" sx={{ mb: 0.75 }}>
              <Typography variant="caption" color="text.secondary">
                <strong>{row.name}:</strong>
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.25 }}>
                {uniqueBlackoutOverlaps.map((blackoutDate, index) => (
                  <Typography
                    key={`${row.id}:${blackoutDate.name}:${blackoutDate.from__c}:${blackoutDate.to__c}:${index}`}
                    component="li"
                    variant="caption"
                    color="text.secondary"
                  >
                    {formatBlackoutRange(blackoutDate)}
                  </Typography>
                ))}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

function formatBlackoutRange(blackoutDate: BlackoutDateRange) {
  return `${blackoutDate.name} (${formatContextDate(blackoutDate.from__c)} - ${formatContextDate(blackoutDate.to__c)})`;
}

function dedupeBlackoutOverlaps(blackoutOverlaps: SurchargePeriodRowView['blackoutOverlaps']) {
  return uniqueBy(
    blackoutOverlaps,
    (blackoutDate) => `${blackoutDate.name}:${blackoutDate.from__c}:${blackoutDate.to__c}`,
  );
}
